import '../scss/store-locator.scss';

const config = window.theraskinStoreLocator || {};
const t = (key, fallback) => (config.i18n && config.i18n[key]) || fallback;

document.addEventListener('DOMContentLoaded', () => {
	const form = document.getElementById('store-locator-form');
	const mapEl = document.getElementById('store-locator-map');
	const resultsEl = document.getElementById('store-locator-results');
	const statusEl = document.getElementById('store-locator-status');
	const cepInput = document.getElementById('store-locator-cep');
	const addressInput = document.getElementById('store-locator-address');
	const geoBtn = document.getElementById('store-locator-geo');

	if (!form || !mapEl || !geoBtn || !resultsEl || !statusEl || !cepInput || !addressInput) return;

	const productId = parseInt(config.productId, 10) || 0;
	const productTitle = config.productTitle ? String(config.productTitle) : '';

	/** Approximate Brazil bounding box (south, west / north, east). */
	const BRAZIL_BOUNDS = [
		[-33.9, -74.0],
		[5.3, -28.6],
	];

	const map = L.map(mapEl);
	map.fitBounds(BRAZIL_BOUNDS);
	L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
		attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
		maxZoom: 19,
	}).addTo(map);

	const invalidate = () => map.invalidateSize();
	window.addEventListener('resize', invalidate);
	requestAnimationFrame(invalidate);

	const layers = L.layerGroup().addTo(map);
	let origin = null;
	let markersById = {};

	form.querySelectorAll('input[name="store-locator-radius"]').forEach((input) => {
		input.addEventListener('change', () => {
			if (origin) {
				searchStores(origin.lat, origin.lng).catch((error) => setStatus(error.message, true));
			}
		});
	});

	cepInput.addEventListener('input', () => {
		const digits = cepInput.value.replace(/\D/g, '').slice(0, 8);
		cepInput.value = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
		syncGeoBtn();
	});

	addressInput.addEventListener('input', syncGeoBtn);

	form.addEventListener('submit', (event) => {
		event.preventDefault();
		runSearch();
	});

	geoBtn.addEventListener('click', () => {
		cepInput.value = '';
		addressInput.value = '';
		syncGeoBtn();
	});

	resultsEl.addEventListener('click', (event) => {
		if (event.target.closest('a')) return;
		const item = event.target.closest('[data-store-id]');
		if (item) focusStore(item.dataset.storeId);
	});

	function useGeo() {
		return !cepInput.value.replace(/\D/g, '') && !addressInput.value.trim();
	}

	function syncGeoBtn() {
		geoBtn.setAttribute('aria-pressed', useGeo() ? 'true' : 'false');
	}

	function selectedRadius() {
		const selected = form.querySelector('input[name="store-locator-radius"]:checked');
		return selected ? parseInt(selected.value, 10) : 10;
	}

	function setStatus(message, isError = false) {
		statusEl.textContent = message || '';
		statusEl.classList.toggle('store-locator__status--error', Boolean(message && isError));
	}

	/** Page load: GPS (if available) → IP. On failure, wait for Buscar. */
	async function tryAutoSearch() {
		setStatus(t('searching', 'Buscando lojas...'));
		try {
			const coords = await locateByGeoOrIp();
			await searchStores(coords.lat, coords.lng);
		} catch (e) {
			setStatus('');
		}
	}

	/** Manual Buscar: CEP → endereço → GPS/IP (with visible errors). */
	async function runSearch() {
		setStatus(t('searching', 'Buscando lojas...'));

		try {
			const cep = cepInput.value.replace(/\D/g, '');
			const address = addressInput.value.trim();
			const coords = cep.length === 8
				? await geocode({ cep })
				: address
					? await geocode({ address })
					: await locateByGeoOrIp();

			await searchStores(coords.lat, coords.lng);
		} catch (error) {
			setStatus(error.message, true);
		}
	}

	async function locateByGeoOrIp() {
		if (window.isSecureContext && navigator.geolocation) {
			try {
				const coords = await getCurrentPosition();
				if (isInBrazil(coords.lat, coords.lng)) {
					return coords;
				}
			} catch (e) {
				// fall through to IP
			}
		}

		return fetchIpGeo();
	}

	function isInBrazil(lat, lng) {
		const [[south, west], [north, east]] = BRAZIL_BOUNDS;
		return lat >= south && lat <= north && lng >= west && lng <= east;
	}

	async function geocode({ cep, address }) {
		const url = new URL(`${config.restUrl}cep`, window.location.origin);
		if (cep) {
			url.searchParams.set('cep', cep);
		} else {
			url.searchParams.set('address', address);
		}

		const data = await fetchJson(url);
		const lat = parseFloat(data.lat);
		const lng = parseFloat(data.lng);

		if (Number.isNaN(lat) || Number.isNaN(lng)) {
			throw new Error(cep ? t('errorCep', 'CEP não encontrado.') : t('errorAddress', 'Endereço não encontrado.'));
		}

		return { lat, lng };
	}

	function getCurrentPosition() {
		return new Promise((resolve, reject) => {
			navigator.geolocation.getCurrentPosition(
				(position) => resolve({
					lat: position.coords.latitude,
					lng: position.coords.longitude,
				}),
				() => reject(new Error('geo')),
				{ timeout: 8000, maximumAge: 0 }
			);
		});
	}

	async function fetchIpGeo() {
		const data = await fetchJson(new URL(`${config.restUrl}geo/ip`, window.location.origin));
		const lat = parseFloat(data.lat);
		const lng = parseFloat(data.lng);

		if (Number.isNaN(lat) || Number.isNaN(lng)) {
			throw new Error(t('errorGeoFallback', 'Não foi possível obter localização. Informe CEP ou endereço.'));
		}

		return { lat, lng };
	}

	async function searchStores(lat, lng) {
		const radius = selectedRadius();
		const data = await fetchJson(`${config.restUrl}stores/search`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ lat, lng, radius, product_id: productId }),
		});

		origin = { lat, lng };
		const stores = (data && data.stores) || [];
		updateMap(stores, origin, radius);
		renderResults(stores, radius);
		setStatus('');
	}

	async function fetchJson(url, options = {}) {
		const headers = { Accept: 'application/json', 'X-WP-Nonce': config.nonce, ...(options.headers || {}) };
		const response = await fetch(String(url), { ...options, headers });

		let data = null;
		try {
			data = await response.json();
		} catch (e) {
			data = null;
		}

		if (!response.ok) {
			throw new Error((data && data.message) || t('errorSearch', 'Erro ao buscar lojas. Tente novamente.'));
		}

		return data || {};
	}

	function updateMap(stores, point, radiusKm) {
		layers.clearLayers();
		markersById = {};

		const circle = L.circle([point.lat, point.lng], {
			radius: radiusKm * 1000,
			color: '#af2323',
			weight: 2,
			opacity: 0.85,
			fillColor: '#af2323',
			fillOpacity: 0.15,
		}).addTo(layers);

		L.marker([point.lat, point.lng], { title: 'Sua localização' })
			.bindPopup(`<strong>Sua localização</strong><br>${radiusKm} ${t('km', 'km')}`)
			.addTo(layers);

		const bounds = circle.getBounds();

		stores.forEach((store) => {
			const marker = L.marker([store.lat, store.lng])
				.bindPopup(
					`<strong>${escapeHtml(store.name)}</strong><br>${escapeHtml(formatAddress(store))}${store.phone ? `<br>${escapeHtml(store.phone)}` : ''}<br>${store.distance} ${t('km', 'km')}`
				)
				.addTo(layers);

			markersById[String(store.id)] = marker;
			bounds.extend([store.lat, store.lng]);
		});

		invalidate();
		map.fitBounds(bounds, { padding: [32, 32], maxZoom: 13 });
	}

	function renderResults(stores, radiusKm) {
		const km = t('km', 'km');

		if (!stores.length) {
			resultsEl.innerHTML = `
				<p class="store-locator__results-summary">
					${escapeHtml(t('noResultsInRadius', 'Nenhuma loja encontrada neste raio'))} (${radiusKm} ${km})
				</p>
			`;
			return;
		}

		const productHint = productTitle
			? ` ${escapeHtml(t('ofProduct', 'de'))} ${escapeHtml(productTitle)}`
			: '';

		resultsEl.innerHTML = `
			<h3 class="h5-title c-red mb-2">Resultados</h3>
			<p class="store-locator__results-summary mb-3">
				${stores.length} ${escapeHtml(t('storesLabel', 'loja(s)'))}${productHint} ${escapeHtml(t('withinRadius', 'dentro de'))} ${radiusKm} ${km}
			</p>
			${stores.map((store) => {
				const phone = store.phone ? String(store.phone).trim() : '';
				const phoneHtml = phone
					? `<div class="store-locator__result-phone"><a href="${escapeHtml(telHref(phone))}">${escapeHtml(phone)}</a></div>`
					: '';

				return `
					<article class="store-locator__result-item" data-store-id="${escapeHtml(String(store.id))}">
						<div class="store-locator__result-name">${escapeHtml(store.name)}</div>
						<div class="store-locator__result-distance">${store.distance} ${km}</div>
						<div class="store-locator__result-address">${escapeHtml(formatAddress(store))}</div>
						${phoneHtml}
						<a href="${escapeHtml(directionsUrl(store))}" class="store-locator__result-link" target="_blank" rel="noopener noreferrer">
							${escapeHtml(t('directions', 'Como chegar'))}
						</a>
					</article>
				`;
			}).join('')}
		`;
	}

	function focusStore(storeId) {
		const marker = markersById[String(storeId)];
		if (!marker) return;

		resultsEl.querySelectorAll('.store-locator__result-item').forEach((el) => {
			el.classList.toggle('store-locator__result-item--active', String(el.dataset.storeId) === String(storeId));
		});

		map.setView(marker.getLatLng(), 15);
		marker.openPopup();
	}

	function directionsUrl(store) {
		const params = new URLSearchParams({
			api: '1',
			destination: `${store.lat},${store.lng}`,
		});

		if (origin) {
			params.set('origin', `${origin.lat},${origin.lng}`);
		}

		return `https://www.google.com/maps/dir/?${params.toString()}`;
	}

	function formatAddress(store) {
		return [store.address, store.city, store.state].filter(Boolean).join(', ');
	}

	function telHref(phone) {
		const digits = String(phone).replace(/\D/g, '');
		return digits ? `tel:+55${digits.replace(/^55/, '')}` : '#';
	}

	function escapeHtml(text) {
		const div = document.createElement('div');
		div.textContent = text;
		return div.innerHTML;
	}

	syncGeoBtn();
	tryAutoSearch();
});
